import { createSlice } from '@reduxjs/toolkit';
import {
  login,
  logout,
  removeUserAccountAccess,
  submitOrder,
  updateOrder,
  saveUserAccountChanges,
  editUserData,
  updateInstantSurvey,
  createInstantSurvey,
} from '../api';
import { ERROR_DURATION } from 'contexts/NotificationsProvider';
import SupportButton from 'components/Shared/SupportButton';
import { IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import i18nInitialState from 'config/translations';
import { getMetrics } from './chartsSlice';
import { SNACKBAR_VARIANTS } from '../constants';

const snackbarSlice = createSlice({
  name: 'snackbar',
  initialState: { notifications: [] },
  reducers: {
    enqueueSnackbar: {
      reducer: (state, payload) => {
        state.notifications.push(payload.payload);
      },
      prepare(notification) {
        const payload = {
          ...notification,
          key: new Date().getTime() + Math.random(),
        };
        return { payload };
      },
    },
    closeSnackbar: {
      reducer: (state, action) => {
        const { payload } = action;
        // eslint-disable-next-line no-param-reassign
        state.notifications = state.notifications.map((notification) => {
          const shouldDismiss =
            payload.dismissAll ||
            notification.key === payload.key ||
            // eslint-disable-next-line no-underscore-dangle
            notification.options._key === payload.key;

          return shouldDismiss
            ? { ...notification, dismissed: true }
            : { ...notification };
        });
      },
      prepare: (key) => ({ payload: { key, dismissAll: !key } }),
    },
    removeSnackbar: (state, { payload }) => {
      // eslint-disable-next-line no-param-reassign
      state.notifications = state.notifications.filter(
        (notification) => notification.key !== payload,
      );
    },
  },
  extraReducers(builder) {
    builder
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload.ok) {
          if (!action.payload.data.data.authMetadata) {
            const payload = {
              message: i18nInitialState.en.errors.doNotHaveAccessToAnyAccounts,
              options: {
                variant: SNACKBAR_VARIANTS.error,
                autoHideDuration: ERROR_DURATION,
              },
              key: new Date().getTime() + Math.random(),
            };
            state.notifications.push(payload);
          }
        } else {
          const options = action.payload && {
            correlationUUID: action.payload.correlationUUID,
          };
          switch (action.payload.status) {
            case 422: {
              const payload = {
                message: i18nInitialState.en.errors.validationError,
                options: {
                  variant: SNACKBAR_VARIANTS.error,
                  autoHideDuration: ERROR_DURATION,
                },
                key: new Date().getTime() + Math.random(),
              };
              state.notifications.push(payload);
              break;
            }
            case 500: {
              switch (action.payload.errorCode) {
                case 1: {
                  const payload = {
                    message: i18nInitialState.en.userNotFound,
                    options: {
                      variant: SNACKBAR_VARIANTS.error,
                      autoHideDuration: ERROR_DURATION,
                    },
                    key: new Date().getTime() + Math.random(),
                  };
                  state.notifications.push(payload);
                  break;
                }
                case 2: {
                  const payload = {
                    message: i18nInitialState.en.errors.credentialsInvalid,
                    options: {
                      variant: SNACKBAR_VARIANTS.error,
                      autoHideDuration: ERROR_DURATION,
                    },
                    key: new Date().getTime() + Math.random(),
                  };
                  state.notifications.push(payload);
                  break;
                }
                default: {
                  const payload = {
                    message: i18nInitialState.en.errors.unknownError,
                    options: {
                      variant: SNACKBAR_VARIANTS.error,
                      autoHideDuration: ERROR_DURATION,
                      action: options
                        ? (key) => (
                          <>
                            <SupportButton options={options} />
                            <IconButton
                              onClick={() => {
                                // closeSnackbar will be handled by parent component
                                console.log('Close snackbar:', key);
                              }}
                              size='small'
                            >
                              <CloseIcon />
                            </IconButton>
                          </>
                        )
                        : undefined,
                    },
                    key: new Date().getTime() + Math.random(),
                  };
                  state.notifications.push(payload);
                  break;
                }
              }
              break;
            }
            default: {
              const payload = {
                message: '',
                options: {
                  variant: SNACKBAR_VARIANTS.error,
                  autoHideDuration: ERROR_DURATION,
                  action: options
                    ? (key) => (
                      <>
                        <SupportButton options={options} />
                        <IconButton
                          onClick={() => {
                            // closeSnackbar will be handled by parent component
                            console.log('Close snackbar:', key);
                          }}
                          size='small'
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                    )
                    : undefined,
                },
                key: new Date().getTime() + Math.random(),
              };
              state.notifications.push(payload);
              break;
            }
          }
        }
      })
      .addCase(logout.fulfilled, (state, action) => {
        if (action.payload.ok) {
        } else {
          const options = action.payload && {
            correlationUUID: action.payload.correlationUUID,
          };
          const payload = {
            message: i18nInitialState.en.errors.unknownError,
            options: {
              variant: SNACKBAR_VARIANTS.error,
              autoHideDuration: ERROR_DURATION,
              action: options
                ? (key) => (
                  <>
                    <SupportButton options={options} />
                    <IconButton
                      onClick={() => {
                        // closeSnackbar will be handled by parent component
                        console.log('Close snackbar:', key);
                      }}
                      size='small'
                    >
                      <CloseIcon />
                    </IconButton>
                  </>
                )
                : undefined,
            },
            key: new Date().getTime() + Math.random(),
          };
          state.notifications.push(payload);
        }
      })
      .addCase(getMetrics.fulfilled, (state, action) => {
        if (action.payload.ok) {
        } else {
          const options = action.payload && {
            correlationUUID: action.payload.correlationUUID,
            // userUUID: user.uuid,
          };
          const payload = {
            message: i18nInitialState.en.errors.unknownError,
            options: {
              variant: SNACKBAR_VARIANTS.error,
              autoHideDuration: ERROR_DURATION,
              action: options
                ? (key) => (
                  <>
                    <SupportButton options={options} />
                    <IconButton
                      onClick={() => {
                        // closeSnackbar will be handled by parent component
                        console.log('Close snackbar:', key);
                      }}
                      size='small'
                    >
                      <CloseIcon />
                    </IconButton>
                  </>
                )
                : undefined,
            },
            key: new Date().getTime() + Math.random(),
          };
          state.notifications.push(payload);
        }
      })
      .addCase(submitOrder.fulfilled, (state, action) => {
        const payload = {
          message: i18nInitialState.en.notifications.orderCreated,
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(submitOrder.rejected, (state, action) => {
        const error = action.payload;
        let message = '';
        let options = {
          variant: SNACKBAR_VARIANTS.error,
          autoHideDuration: ERROR_DURATION,
        };

        if (error?.status === 422) {
          if (error?.errors?.status === 'The specified condition was not met for \'status\'.') {
            message = i18nInitialState.en.errors.specifiedCondition;
          } else {
            message = i18nInitialState.en.errors.validationError;
          }
        } else {
          message = '';
          const supportOptions = error?.correlationUUID ? {
            correlationUUID: error.correlationUUID,
          } : undefined;

          options = {
            ...options,
            action: supportOptions
              ? (key) => (
                <>
                  <SupportButton options={supportOptions} />
                  <IconButton
                    onClick={() => {
                      console.log('Close snackbar:', key);
                    }}
                    size='small'
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              )
              : undefined,
          };
        }

        const payload = {
          message,
          options,
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        // Don't show notification if there were no changes
        if (action.payload.noChanges) {
          return;
        }

        const payload = {
          message: 'Order updated successfully',
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(updateOrder.rejected, (state, action) => {
        const error = action.payload;
        let message = '';
        let options = {
          variant: SNACKBAR_VARIANTS.error,
          autoHideDuration: ERROR_DURATION,
        };

        if (error?.status === 422) {
          message = i18nInitialState.en.errors.validationError;
        } else {
          // For other errors show standard message with support
          message = '';
          const supportOptions = error?.correlationUUID ? {
            correlationUUID: error.correlationUUID,
            userUUID: error.userUUID,
          } : undefined;

          options = {
            ...options,
            action: supportOptions
              ? (key) => (
                <>
                  <SupportButton options={supportOptions} />
                  <IconButton
                    onClick={() => {
                      console.log('Close snackbar:', key);
                    }}
                    size='small'
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              )
              : undefined,
          };
        }

        const payload = {
          message,
          options,
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(removeUserAccountAccess.fulfilled, (state, action) => {
        const payload = {
          message: 'Account access removed successfully',
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(removeUserAccountAccess.rejected, (state, action) => {
        const error = action.payload;
        let message = '';

        // Handle specific error cases like in RemoveRowDialog
        switch (error?.status) {
          case 404:
            message = i18nInitialState.en.errors.accountCouldNotFind || 'Account could not be found';
            break;
          case 500:
            if (error?.payload?.errorCode === 1) {
              const { accountId } = error;
              // We would need account name from the error payload for better UX
              message = 'Access to account cannot be removed';
            } else {
              message = i18nInitialState.en.errors.somethingWentWrong || 'Something went wrong';
            }
            break;
          default:
            message = i18nInitialState.en.errors.somethingWentWrong || 'Something went wrong';
            break;
        }

        const payload = {
          message,
          options: {
            variant: SNACKBAR_VARIANTS.error,
            autoHideDuration: ERROR_DURATION,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(saveUserAccountChanges.fulfilled, (state, action) => {
        // Don't show notification if there were no changes (like in updateOrder)
        if (action.payload.noChanges) {
          return;
        }

        const payload = {
          message: 'Account changes saved successfully',
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(saveUserAccountChanges.rejected, (state, action) => {
        const error = action.payload;
        let message = '';

        switch (error?.status) {
          case 422:
            message = i18nInitialState.en.errors.validationError || 'Validation error';
            break;
          default:
            message = i18nInitialState.en.errors.somethingWentWrong || 'Something went wrong';
            break;
        }

        const payload = {
          message,
          options: {
            variant: SNACKBAR_VARIANTS.error,
            autoHideDuration: ERROR_DURATION,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(editUserData.fulfilled, (state, action) => {
        // Don't show notification if there were no changes
        if (action.payload.noChanges) {
          return;
        }

        const payload = {
          message: 'User updated successfully',
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(editUserData.rejected, (state, action) => {
        const error = action.payload;
        let message = '';

        switch (error?.status) {
          case 422:
            message = i18nInitialState.en.errors.validationError || 'Validation error';
            break;
          default:
            message = i18nInitialState.en.errors.somethingWentWrong || 'Something went wrong';
            break;
        }

        const payload = {
          message,
          options: {
            variant: SNACKBAR_VARIANTS.error,
            autoHideDuration: ERROR_DURATION,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(createInstantSurvey.fulfilled, (state, action) => {
        if (action.payload.success) {
          const payload = {
            message: 'Instant survey created successfully',
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
            key: new Date().getTime() + Math.random(),
          };
          state.notifications.push(payload);
        }
      })
      .addCase(createInstantSurvey.rejected, (state, action) => {
        const error = action.payload;
        const payload = {
          message: error?.error || 'Failed to create instant survey',
          options: {
            variant: SNACKBAR_VARIANTS.error,
            autoHideDuration: ERROR_DURATION,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
      .addCase(updateInstantSurvey.fulfilled, (state, action) => {
        if (action.payload.success) {
          const payload = {
            message: 'Survey updated successfully',
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
            key: new Date().getTime() + Math.random(),
          };
          state.notifications.push(payload);
        }
      })
      .addCase(updateInstantSurvey.rejected, (state, action) => {
        const error = action.payload;
        const payload = {
          message: error?.error || 'Failed to update survey',
          options: {
            variant: SNACKBAR_VARIANTS.error,
            autoHideDuration: ERROR_DURATION,
          },
          key: new Date().getTime() + Math.random(),
        };
        state.notifications.push(payload);
      })
  },
});

export const {
  enqueueSnackbar,
  closeSnackbar,
  removeSnackbar,
} = snackbarSlice.actions;

export default snackbarSlice.reducer;
